import type { HTMLAttributes } from 'react'

import { cn } from './cn'

/**
 * 标签。设计稿里到处都是：机器标签、项目归属、状态、风险级。
 *
 * `tone` 与文案模板里的 `tone` 字段一一对应，
 * 所以后端说这条是 `warn`，前端直接 `<Tag tone="warn">` 就完了，
 * 不需要在前端再判断一次「什么情况算警告」——那是会两边走偏的地方。
 */
export type TagTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info'

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: TagTone
}

const tones: Record<TagTone, string> = {
  neutral: 'bg-npc-surface text-npc-ink-medium',
  ok: 'bg-npc-green-bg text-npc-green-deep',
  warn: 'bg-npc-amber-bg text-npc-amber-ink',
  danger: 'bg-npc-red-bg text-npc-red-ink',
  info: 'bg-npc-blue-bg text-npc-blue-ink',
}

export function Tag({ tone = 'neutral', className, ...rest }: TagProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-npc-tag-sm px-npc-tag-x py-npc-tag-y text-npc-caption-sm whitespace-nowrap',
        tones[tone],
        className,
      )}
      {...rest}
    />
  )
}

/**
 * 状态点。设计稿里「● 全部在线」那个圆点。
 *
 * `pulsing` 打开时跑 nwpulse，表示「正在进行」。
 */
export function StatusDot({
  tone = 'ok',
  pulsing = false,
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: TagTone; pulsing?: boolean }) {
  const colors: Record<TagTone, string> = {
    neutral: 'bg-npc-ink-faint',
    ok: 'bg-npc-green',
    warn: 'bg-npc-amber',
    danger: 'bg-npc-red',
    info: 'bg-npc-blue',
  }
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-block size-[var(--container-npc-status-dot)] rounded-npc-full shrink-0',
        colors[tone],
        pulsing && 'animate-npc-pulse',
        className,
      )}
      {...rest}
    />
  )
}
